/**
 * Applications Copilot — one chat message (v2 "glass").
 *
 * User messages: right-aligned gradient bubble (`.cp-bubble-user`, max 85%).
 * Assistant messages: 24 px orb avatar (conic "thinking" ring while this
 * message streams) + a glass card (`.cp-bubble-assistant`) holding:
 *   - the thinking stepper (status lines → 16 px circles; collapses to one
 *     line "Checked n sources · 15.4 s" once the answer starts),
 *   - a 3-line shimmer skeleton while a tool runs and nothing has streamed,
 *   - streamed text through a tiny SAFE markdown renderer (bold, italic,
 *     inline code, bullet / numbered lists, line breaks; no HTML, no
 *     dangerouslySetInnerHTML) with a mint caret while streaming and
 *     `[Name]` citation chips,
 *   - the structured blocks (`cp-fade-up`),
 *   - an inline error card with Retry (partial content stays above),
 *   - a "Stopped" tag when cancelled,
 * then a muted footer row: thumbs up/down, Copy, Read aloud/Stop, latency,
 * model pill and "How I got this" (the stepper in plain language, per-tool ms).
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
  Volume2,
  Square,
  ChevronDown,
  ChevronRight,
  Loader2,
  RotateCcw,
  AlertCircle,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import CopilotBlocks, { IconButton } from './CopilotBlocks';
import { sendFeedback } from './copilotApi';
import { useCopilot } from './CopilotContext';
import CopilotAvatar from './CopilotAvatar';

// ---------------------------------------------------------------------------
// Safe light markdown renderer
// ---------------------------------------------------------------------------

/**
 * Split text into inline tokens: bold (**x**), italic (*x* / _x_), code (`x`)
 * and citation candidates ([Label]). Everything else is plain text.
 */
export function tokenizeInline(text) {
  const tokens = [];
  const re = /(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\[[^\]\n]{1,80}\])|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  let last = 0;
  let m = re.exec(text);
  while (m) {
    if (m.index > last) tokens.push({ type: 'text', value: text.slice(last, m.index) });
    if (m[1]) tokens.push({ type: 'bold', value: m[1].slice(2, -2) });
    else if (m[2]) tokens.push({ type: 'code', value: m[2].slice(1, -1) });
    else if (m[3]) tokens.push({ type: 'cite', value: m[3].slice(1, -1) });
    else if (m[4]) tokens.push({ type: 'italic', value: m[4].slice(1, -1) });
    else if (m[5]) tokens.push({ type: 'italic', value: m[5].slice(1, -1) });
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) tokens.push({ type: 'text', value: text.slice(last) });
  return tokens;
}

/** Group lines into paragraph / bullet-list / numbered-list / heading blocks. */
export function parseMarkdownBlocks(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', lines: para });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };

  lines.forEach((rawLine) => {
    const line = rawLine.replace(/\s+$/, '');
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line);

    if (!line.trim()) {
      flushPara();
      flushList();
      return;
    }
    if (heading) {
      flushPara();
      flushList();
      blocks.push({ type: 'h', text: heading[1] });
      return;
    }
    if (bullet) {
      flushPara();
      if (!list || list.type !== 'ul') {
        flushList();
        list = { type: 'ul', items: [] };
      }
      list.items.push(bullet[1]);
      return;
    }
    if (numbered) {
      flushPara();
      if (!list || list.type !== 'ol') {
        flushList();
        list = { type: 'ol', items: [], start: Number(numbered[1]) || 1 };
      }
      list.items.push(numbered[2]);
      return;
    }
    if (list && /^\s{2,}/.test(rawLine)) {
      // continuation of previous list item
      list.items[list.items.length - 1] += ` ${line.trim()}`;
      return;
    }
    flushList();
    para.push(line);
  });
  flushPara();
  flushList();
  return blocks;
}

function CitationChip({ label, citation, onClick, compact = false }) {
  const isCandidate = citation.kind === 'candidate';
  return (
    <button
      type="button"
      onClick={() => onClick(citation)}
      title={compact ? `${label} — ${isCandidate ? 'locate in list' : 'open'}` : isCandidate ? 'Locate in list' : 'Open'}
      aria-label={`${label} — ${isCandidate ? 'locate in list' : 'open'}`}
      data-citation-kind={citation.kind}
      data-compact={compact ? 'true' : undefined}
      className={cn('cp-chip cp-chip-mint cp-chip-sm mx-0.5 font-medium', compact && 'px-1')}
    >
      <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {!compact && <span>{label}</span>}
    </button>
  );
}

/** True when the token before an "[#id]" marker already ends with that candidate's name. */
function precededByName(tokens, index, label) {
  if (!label) return false;
  let j = index - 1;
  // skip whitespace-only text tokens between the name and the marker
  while (j >= 0 && tokens[j].type === 'text' && !String(tokens[j].value || '').trim()) j -= 1;
  const prev = tokens[j];
  if (!prev || (prev.type !== 'text' && prev.type !== 'bold')) return false;
  return String(prev.value || '').trim().toLowerCase().endsWith(String(label).trim().toLowerCase());
}

function InlineTokens({ text, citationIndex, onCitation }) {
  const tokens = useMemo(() => tokenizeInline(text), [text]);
  return (
    <>
      {tokens.map((tok, i) => {
        switch (tok.type) {
          case 'bold':
            return (
              <strong key={i} className="font-semibold">
                {tok.value}
              </strong>
            );
          case 'italic':
            return <em key={i}>{tok.value}</em>;
          case 'code':
            return (
              <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[12px]">
                {tok.value}
              </code>
            );
          case 'cite': {
            const raw = tok.value.trim();
            const isIdRef = /^#\d{1,12}$/.test(raw);
            const citation = citationIndex.get(isIdRef ? raw : raw.toLowerCase());
            if (citation) {
              return (
                <CitationChip
                  key={i}
                  label={isIdRef ? citation.label || raw : tok.value}
                  citation={citation}
                  onClick={onCitation}
                  compact={isIdRef && precededByName(tokens, i, citation.label)}
                />
              );
            }
            // Unknown "[#id]" marker: show a quiet reference instead of raw brackets.
            if (isIdRef) return <span key={i} className="cp-faint text-[11px] align-super">{raw}</span>;
            return <React.Fragment key={i}>[{tok.value}]</React.Fragment>;
          }
          default:
            return <React.Fragment key={i}>{tok.value}</React.Fragment>;
        }
      })}
    </>
  );
}

/**
 * @param {object} props
 * @param {string} props.text        markdown-ish text
 * @param {Array}  [props.citations] `{kind, application_id, label}` items
 * @param {boolean} [props.caret]    append a blinking mint caret to the last text node
 */
export function MarkdownText({ text, citations = [], className, caret = false }) {
  const copilot = useCopilot();
  const citationIndex = useMemo(() => {
    const map = new Map();
    (citations || []).forEach((c) => {
      if (!c) return;
      if (c.label) map.set(String(c.label).trim().toLowerCase(), c);
      // The model cites candidates as "[#<application_id>]" (see prompts.py);
      // index by id so those markers become chips too.
      if (c.application_id != null) map.set(`#${c.application_id}`, c);
    });
    return map;
  }, [citations]);

  const onCitation = useCallback(
    (citation) => {
      if (!citation) return;
      if (citation.kind === 'candidate') copilot.locateCandidate(citation.application_id);
      else copilot.viewProfile(citation.application_id);
    },
    [copilot]
  );

  const blocks = useMemo(() => parseMarkdownBlocks(text), [text]);
  if (!blocks.length) return null;
  const lastIndex = blocks.length - 1;

  return (
    <div className={cn('space-y-1.5 text-[13px] leading-relaxed text-foreground break-words', className)}>
      {blocks.map((block, bi) => {
        const isLast = caret && bi === lastIndex;
        if (block.type === 'h') {
          return (
            <div key={bi} className={cn('font-semibold text-foreground', isLast && 'cp-caret')}>
              <InlineTokens text={block.text} citationIndex={citationIndex} onCitation={onCitation} />
            </div>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const Tag = block.type;
          const lastItem = block.items.length - 1;
          return (
            <Tag
              key={bi}
              start={block.type === 'ol' ? block.start : undefined}
              className={cn('pl-4 space-y-0.5', block.type === 'ul' ? 'list-disc' : 'list-decimal')}
            >
              {block.items.map((item, ii) => (
                <li key={ii} className={cn(isLast && ii === lastItem && 'cp-caret')}>
                  <InlineTokens text={item} citationIndex={citationIndex} onCitation={onCitation} />
                </li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={bi} className={cn(isLast && 'cp-caret')}>
            {block.lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                <InlineTokens text={line} citationIndex={citationIndex} onCitation={onCitation} />
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thinking stepper
// ---------------------------------------------------------------------------

function formatLatency(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${(n / 1000).toFixed(1)} s`;
}

function stepMs(step) {
  const n = Number(step?.ms ?? step?.latency_ms ?? step?.duration_ms);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

/**
 * Vertical list of steps. `activeIndex` (if any) shows a spinner; every other
 * step shows a check.
 */
function StepList({ steps, activeIndex = -1, className }) {
  if (!steps.length) return null;
  return (
    <ol className={cn('space-y-1', className)}>
      {steps.map((step, i) => {
        const active = i === activeIndex;
        return (
          <li key={`${step.text}-${i}`} className="cp-step text-xs cp-muted">
            <span className="cp-step-dot" data-done={active ? undefined : 'true'} aria-hidden="true">
              {active ? (
                <Loader2 className="h-2.5 w-2.5 motion-safe:animate-spin" />
              ) : (
                <Check className="h-2.5 w-2.5" />
              )}
            </span>
            <span className="min-w-0 flex-1 leading-4">
              <span className={cn(active && 'text-foreground')}>{step.text}</span>
              {step.detail && <span className="cp-faint"> · {step.detail}</span>}
              {step.ms !== null && step.ms !== undefined && (
                <span className="cp-num cp-faint"> · {step.ms} ms</span>
              )}
            </span>
            {active && <span className="sr-only">(in progress)</span>}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Thinking stepper. Same props as the v1 status list, plus optional
 * `toolTrace` / `latencyMs` for the collapsed summary.
 *
 * - streaming, no content yet → full vertical stepper (spinner on the latest step)
 * - content exists (streaming or not) → one line "Checked n sources · 15.4 s"
 *   with a chevron that expands the list
 * - finished and nothing to show → null (the footer's "How I got this" takes over)
 */
export function StatusLines({ lines = [], isStreaming, hasContent, toolTrace, latencyMs }) {
  const [open, setOpen] = useState(false);
  const steps = useMemo(() => (lines || []).map((text) => ({ text, ms: null })), [lines]);
  if (!steps.length) return null;
  if (!isStreaming && hasContent) return null;

  if (!hasContent) {
    return (
      <div aria-live="polite" aria-busy={isStreaming ? 'true' : undefined}>
        <StepList steps={steps} activeIndex={isStreaming ? steps.length - 1 : -1} />
      </div>
    );
  }

  const sourceCount = Array.isArray(toolTrace) && toolTrace.length ? toolTrace.length : steps.length;
  const latency = formatLatency(latencyMs);
  const summary = `Checked ${sourceCount} ${sourceCount === 1 ? 'source' : 'sources'}${latency ? ` · ${latency}` : ''}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="cp-focus inline-flex items-center gap-1 rounded text-[11px] cp-muted hover:text-foreground"
        data-testid="stepper-summary"
      >
        {open ? <ChevronDown className="h-3 w-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3" aria-hidden="true" />}
        {isStreaming && !latency ? (
          <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden="true" />
        ) : (
          <Check className="h-3 w-3" aria-hidden="true" />
        )}
        <span className="cp-num">{summary}</span>
      </button>
      {open && <StepList steps={steps} activeIndex={isStreaming ? steps.length - 1 : -1} className="mt-1.5" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "How I got this" (footer) — the stepper in plain language with per-tool ms
// ---------------------------------------------------------------------------

function HowIGotThis({ trace, statusLines }) {
  const [open, setOpen] = useState(false);
  const steps = useMemo(() => {
    if (Array.isArray(trace) && trace.length) {
      return trace.map((step) => ({
        text: step.summary || step.tool || 'Step',
        detail: step.summary && step.tool ? step.tool : null,
        ms: stepMs(step),
      }));
    }
    return (statusLines || []).map((text) => ({ text, ms: null }));
  }, [trace, statusLines]);
  if (!steps.length) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="cp-focus ml-auto inline-flex h-7 items-center gap-1 rounded px-1.5 text-[11px] cp-muted hover:text-foreground"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3 w-3" aria-hidden="true" /> : <ChevronRight className="h-3 w-3" aria-hidden="true" />}
        How I got this
      </button>
      {open && (
        <div className="basis-full pt-1">
          <StepList steps={steps} />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function Skeleton() {
  return (
    <div className="space-y-2" aria-hidden="true" data-testid="cp-skeleton">
      <div className="cp-shimmer h-3" style={{ width: '90%' }} />
      <div className="cp-shimmer h-3" style={{ width: '70%' }} />
      <div className="cp-shimmer h-3" style={{ width: '50%' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message
// ---------------------------------------------------------------------------

function copyToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error('Clipboard unavailable'));
}

/**
 * @param {object} props
 * @param {object} props.message  { id, role, content, blocks, citations, speech, statusLines, toolTrace, rating, error, isStreaming, cancelled, latencyMs, model }
 * @param {(prompt:string, opts?:object)=>void} [props.onPrompt]
 * @param {(text:string)=>void} [props.onSpeak]
 * @param {()=>void} [props.onStopSpeak]
 * @param {boolean} [props.speaking]
 * @param {()=>void} [props.onRetry]
 */
export default function CopilotMessage({ message, onPrompt, onSpeak, onStopSpeak, speaking = false, onRetry }) {
  const [rating, setRating] = useState(message?.rating ?? null);
  const [copied, setCopied] = useState(false);

  if (!message) return null;

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="cp-bubble-user max-w-[85%] whitespace-pre-wrap break-words px-3 py-1.5 text-[13px]">{message.content}</div>
      </div>
    );
  }

  const content = message.content || '';
  const blocks = Array.isArray(message.blocks) ? message.blocks : [];
  const hasText = Boolean(content.trim());
  const hasContent = hasText || blocks.length > 0;
  const isStreaming = Boolean(message.isStreaming);
  const canRate = message.id && !isStreaming && !message.error;
  const latency = formatLatency(message.latencyMs);

  const handleRate = async (value) => {
    if (!message.id) return;
    const next = rating === value ? null : value;
    setRating(next);
    if (next === null) return;
    try {
      await sendFeedback(message.id, next);
      toast.success('Thanks for the feedback');
    } catch {
      setRating(rating);
      toast.error('Could not send feedback');
    }
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(content);
      setCopied(true);
      toast.success('Copied');
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const speechText = message.speech || content;

  return (
    <div className="flex gap-2">
      <CopilotAvatar
        size={24}
        live={false}
        className={cn('mt-0.5 shrink-0', isStreaming && 'cp-think-ring')}
        title=""
        aria-hidden="true"
        data-streaming={isStreaming ? 'true' : undefined}
      />

      <div className="min-w-0 flex-1 space-y-1">
        <div className="cp-bubble-assistant space-y-2 px-3.5 py-3">
          <StatusLines
            lines={message.statusLines}
            isStreaming={isStreaming}
            hasContent={hasContent}
            toolTrace={message.toolTrace}
            latencyMs={message.latencyMs}
          />

          {isStreaming && !hasContent && (
            <>
              <Skeleton />
              {!message.statusLines?.length && <span className="sr-only">Thinking…</span>}
            </>
          )}

          {hasText && (
            <div aria-live={isStreaming ? 'polite' : undefined} aria-atomic="false">
              <MarkdownText text={content} citations={message.citations} caret={isStreaming} />
            </div>
          )}

          <CopilotBlocks blocks={blocks} onPrompt={onPrompt} readOnly={Boolean(message.fromHistory)} />

          {message.error && (
            <div role="alert" className="cp-banner cp-banner-bad !items-start">
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1 leading-snug">
                {message.error.message}
                {message.error.retryAfter ? ` Try again in ${message.error.retryAfter}s.` : ''}
              </span>
              {message.error.retryable && onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="cp-focus inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-current/40 px-2 text-[11px] font-medium hover:bg-[hsl(var(--cp-bad)/0.12)]"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" /> Retry
                </button>
              )}
            </div>
          )}

          {message.cancelled && !message.error && (
            <span className="inline-flex h-5 items-center gap-1 rounded-full bg-muted px-1.5 text-[11px] font-medium cp-muted">
              <Square className="h-2.5 w-2.5" aria-hidden="true" /> Stopped
            </span>
          )}
        </div>

        {!isStreaming && hasContent && (
          <div className="-ml-1 flex flex-wrap items-center gap-0.5 text-[12px] cp-muted">
            <IconButton
              label="Helpful"
              className="h-7 w-7"
              on={rating === 1}
              onClick={() => handleRate(1)}
              disabled={!canRate}
              aria-pressed={rating === 1}
            >
              <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
            </IconButton>
            <IconButton
              label="Not helpful"
              className={cn('h-7 w-7', rating === -1 && 'text-[hsl(var(--cp-bad))]')}
              onClick={() => handleRate(-1)}
              disabled={!canRate}
              aria-pressed={rating === -1}
            >
              <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
            </IconButton>
            <IconButton label={copied ? 'Copied' : 'Copy answer'} className="h-7 w-7" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            </IconButton>
            {onSpeak && speechText && (
              <IconButton
                label={speaking ? 'Stop reading' : 'Read aloud'}
                className="h-7 w-7"
                on={speaking}
                onClick={() => (speaking && onStopSpeak ? onStopSpeak() : onSpeak(speechText))}
              >
                {speaking ? <Square className="h-3.5 w-3.5" aria-hidden="true" /> : <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />}
              </IconButton>
            )}
            {latency && (
              <span className="cp-num cp-faint ml-1 text-[12px]" title="Time to answer">
                {latency}
              </span>
            )}
            {message.model && (
              <span
                className="cp-num cp-faint ml-1 hidden h-[18px] items-center rounded-full border px-1.5 text-[10px] font-medium sm:inline-flex"
                style={{ borderColor: 'var(--cp-glass-border)' }}
                title="Model"
              >
                {message.model}
              </span>
            )}
            <HowIGotThis trace={message.toolTrace} statusLines={message.statusLines} />
          </div>
        )}
      </div>
    </div>
  );
}
